import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Terminal, RefreshCw, Pause, Play, Trash2, Download, Search } from 'lucide-react';
import Sidebar from '@/components/Sidebar';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

const LEVELS = [
  { key: '', label: 'All' },
  { key: 'ERROR', label: 'Error' },
  { key: 'WARNING', label: 'Warning' },
  { key: 'INFO', label: 'Info' },
];

function lineColor(line) {
  if (/ERROR|CRITICAL|Traceback/i.test(line)) return 'text-red-400';
  if (/WARNING/i.test(line)) return 'text-amber-400';
  if (/INFO/i.test(line)) return 'text-zinc-300';
  return 'text-zinc-500';
}

export default function BotLog() {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [level, setLevel] = useState(() => localStorage.getItem('botLogLevel') || '');
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('botLogAutoRefresh') !== 'false');
  const bottomRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('botLogLevel', level);
  }, [level]);

  useEffect(() => {
    localStorage.setItem('botLogAutoRefresh', String(autoRefresh));
  }, [autoRefresh]);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/bot/log`, {
        ...authHeader(),
        params: { lines: 300, level: level || undefined, search: search || undefined },
      });
      setLines(data.lines || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [level, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchLogs, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs]);

  const downloadLog = () => {
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `beatmetrics-log-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="bot-log-main">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-white">Bot Log</h1>
            <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">
              Live systemd journal for the BeatMetrics service — owner only
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search log…"
                data-testid="bot-log-search"
                className="pl-7 pr-2 py-1.5 rounded-lg text-xs font-[Manrope] bg-[var(--bg-hover)] border border-[var(--border)] text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/40 w-36"
              />
            </div>
            {LEVELS.map(l => (
              <button
                key={l.key}
                onClick={() => setLevel(l.key)}
                data-testid={`bot-log-filter-${l.key || 'all'}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium font-[Manrope] border transition-colors ${
                  level === l.key
                    ? 'bg-[#5865F2]/20 border-[#5865F2]/40 text-[#A5B4FC]'
                    : 'bg-[var(--bg-hover)] border-[var(--border)] text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => setAutoRefresh(a => !a)}
              data-testid="bot-log-autorefresh-toggle"
              title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}
              className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-white transition-colors"
            >
              {autoRefresh ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button
              onClick={fetchLogs}
              data-testid="bot-log-refresh-btn"
              title="Refresh now"
              className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-white transition-colors"
            >
              <RefreshCw size={15} />
            </button>
            <button
              onClick={downloadLog}
              disabled={lines.length === 0}
              data-testid="bot-log-download-btn"
              title="Download log"
              className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={15} />
            </button>
            <button
              onClick={() => setLines([])}
              data-testid="bot-log-clear-btn"
              title="Clear view"
              className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-red-400 transition-colors"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
          data-testid="bot-log-console"
        >
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-hover)]">
            <Terminal size={14} className="text-zinc-500" />
            <span className="text-xs text-zinc-500 font-mono-stats">journalctl -u beatmetrics -n 300{level ? ` | grep ${level}` : ''}{search ? ` | grep "${search}"` : ''}</span>
            {autoRefresh && (
              <span className="ml-auto flex items-center gap-1.5 text-[10px] text-emerald-400 font-[Manrope]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="p-4 max-h-[70vh] overflow-y-auto font-mono text-xs leading-relaxed">
            {loading ? (
              <p className="text-zinc-600">Loading log…</p>
            ) : error ? (
              <p className="text-red-400">{error}</p>
            ) : lines.length === 0 ? (
              <p className="text-zinc-600">No log lines.</p>
            ) : (
              lines.map((l, i) => (
                <div key={i} className={`whitespace-pre-wrap break-all ${lineColor(l)}`} data-testid={`bot-log-line-${i}`}>
                  {l}
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>
        </motion.div>
      </main>
    </div>
  );
}
